import React, { useCallback, useContext, useState } from 'react';
import WorkerEventEraseForm from './forms/WorkerEventEraseForm';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonDeleteForm from '../../../components/buttons/ButtonDeleteForm';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import useEventService from '../../../app/services/hooks/useEventService';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { AppContext } from '../../../contexts/AppContext';
import useNavigationPage from '../../../hooks/useNavigationPage';

interface Props {
    eventId: number;
    workerId: number;
    selectedDate: string;
    onCancel?: () => void;
    onError?: () => void;
}

type OptionType = 'one' | 'all' | 'next';

const WorkerEventErase = ({ eventId, workerId, selectedDate, onCancel, onError }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();
    const { deleteShiftWorkEvent, deleteOneDayShiftWorkEvent, deleteNextDaysShiftWorkEvent } =
        useEventService();

    const [selectedOption, setSelectedOption] = useState<OptionType | ''>('');
    const [isLocked, setIsLocked] = useState<boolean>(true);

    const optionConfig: Record<OptionType, { message: string; deleteAction: () => void }> = {
        one: {
            message: 'Está a punto de eliminar la presente fecha',
            deleteAction: () =>
                deleteOneDayShiftWorkEvent(eventId, selectedDate, {
                    onSuccess: onSuccesAction,
                    onError: onErrorAction
                })
        },
        all: {
            message: 'Está a punto de eliminar todas las fechas de esta jornada de trabajo',
            deleteAction: () =>
                deleteShiftWorkEvent(eventId, {
                    onSuccess: onSuccesAction,
                    onError: onErrorAction
                })
        },
        next: {
            message: 'Está a punto de eliminar todas las fechas a partir de la siguiente',
            deleteAction: () =>
                deleteNextDaysShiftWorkEvent(eventId, selectedDate, {
                    onSuccess: onSuccesAction,
                    onError: onErrorAction
                })
        }
    };

    const showConfirmAlert = () => {
        if (selectedOption === '') return;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: optionConfig[selectedOption].message,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando evento...');
                        optionConfig[selectedOption].deleteAction();
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El evento no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const onSuccesAction = (response: ServiceResponse) => {
        if (hideLoading) hideLoading();
        useReactConfirmAlert().successAlert({
            title: 'Éxito',
            message: response.message
        });
        navigationPage(`/workers/${workerId}/edit?defaultActiveKey=calendar`);
    };

    const onErrorAction = (response: ServiceResponse) => {
        if (hideLoading) hideLoading();
        useReactConfirmAlert().errorAlert({
            title: 'Error',
            message: response.message
        });
    };

    const handleOptionChange = useCallback((option: OptionType) => {
        setSelectedOption(option);
        setIsLocked(false);
    }, []);

    const cancel = () => {
        if (onCancel) onCancel();
    };

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <WorkerEventEraseForm eraseMode={selectedOption} erase={handleOptionChange} />
                </div>
            </div>
            <hr />
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm callbackCancel={cancel} />
                </div>
                <div className="col-auto">
                    <ButtonDeleteForm
                        callbackDelete={showConfirmAlert}
                        locked={isLocked}
                        message="Seleccione una opción"
                    />
                </div>
            </div>
        </>
    );
};

export default WorkerEventErase;
